// packages/world-model/src/traits/light-source/lightSourceTrait.ts

import { ITrait } from '../trait.js';
import { TraitType } from '../trait-types.js';
import { IToolGated, ToolRequirement } from '../tool-requirement.js';

/**
 * LightSource trait allows entities to provide illumination.
 * 
 * This is a pure data structure - all validation and logic
 * should be handled by LightSourceBehavior.
 */
export class LightSourceTrait implements ITrait, IToolGated {
  static readonly type = TraitType.LIGHT_SOURCE;
  readonly type = TraitType.LIGHT_SOURCE;
  
  /** Light output level (1-10) */
  brightness: number = 5;

  /**
   * Whether the light is currently providing illumination.
   * - true: explicitly lit
   * - false: explicitly not lit
   * - undefined: use switchable state or default to lit
   */
  isLit?: boolean;
  
  /**
   * The tool that lights it — a match for a lantern (ADR-374 D9). Absent:
   * lighting needs no tool.
   */
  toolRequirement?: ToolRequirement;

  /** Optional fuel/battery remaining (undefined = infinite) */
  fuelRemaining?: number;
  
  /** Maximum fuel capacity (for refillable lights) */
  maxFuel?: number;
  
  /** Fuel consumption rate per turn (when lit) */
  fuelConsumptionRate?: number;

  /** Description when lit (used by computed description getter on IFEntity) */
  litDescription?: string;

  /** Description when unlit (used by computed description getter on IFEntity) */
  unlitDescription?: string;

  /**
   * State detail appended to the base description when lit (ADR-195 S2). Unlike
   * `litDescription` (which *replaces* the description via the computed getter),
   * this is *appended* by the examine `{slot:detail}` channel. Read by the
   * `state-clauses` registry.
   */
  detailWhenLit?: string;

  constructor(data?: Partial<LightSourceTrait>) {
    if (data) {
      Object.assign(this, data);
    }
  }
}
